import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getElementTourRect,
  getTourCardSx,
  getTourCardInnerSx,
} from './tourPosition';

const ACCENT = '#137274';
const PINK = '#f50057';
export const TOUR_STORAGE_KEY = 'ch_product_tour_v1';
export const TOUR_COMPLETE_EVENT = 'ch-tour-complete';
export const TOUR_START_EVENT = 'ch-start-tour';

const steps = [
  {
    id: 'welcome',
    placement: 'center',
    eyebrow: 'Welcome aboard',
    title: 'Your 60-second CrafterHack tour',
    body: 'We’ll show you the fastest path to practice, compete, and interview prep — so you never wonder where to click next.',
    cta: 'Let’s go',
  },
  {
    id: 'hero',
    target: '[data-tour="hero-cta"]',
    placement: 'bottom',
    eyebrow: 'Start here',
    title: 'Pick your first move',
    body: 'Jump into Practice Track for a guided path, browse problems freely, or open Live Coding to pair with someone.',
  },
  {
    id: 'pathways',
    target: '[data-tour="pathways"]',
    placement: 'top',
    eyebrow: 'Four lanes',
    title: 'Choose how you want to grow',
    body: 'Practice Track, Live Coding, Contests, and Mentorship — each path has a clear job. Pick one based on today’s goal.',
  },
  {
    id: 'spotlight',
    target: '[data-tour="spotlight"]',
    placement: 'top',
    eyebrow: 'Stay consistent',
    title: 'Challenges & upcoming contests',
    body: '75 Hard builds a daily streak. The countdown keeps the next contest visible so you never miss a round.',
  },
  {
    id: 'search',
    target: '[data-tour="problem-search"]',
    placement: 'bottom',
    eyebrow: 'Find or surprise',
    title: 'Search — or go Random',
    body: 'Type a title to find a problem fast. Feeling stuck? Hit Random for an unsolved pick that matches your filters.',
  },
  {
    id: 'list',
    target: '[data-tour="problem-list"]',
    placement: 'left',
    eyebrow: 'Scroll smart',
    title: 'Only the list scrolls',
    body: 'Filters and search stay put while you browse. Click any row to open the editor and start solving.',
  },
  {
    id: 'theme',
    target: '[data-tour="theme-toggle"]',
    placement: 'bottom',
    eyebrow: 'Your vibe',
    title: 'Switch light & dark anytime',
    body: 'CrafterHack defaults to dark mode for long coding sessions. Tap here whenever you want light mode instead.',
  },
  {
    id: 'live',
    target: '[data-tour="nav-live"]',
    placement: 'bottom',
    eyebrow: 'Collaborate',
    title: 'Live Coding is one click away',
    body: 'Open a shared room with video, synced code, and run output — great for practice pairs and mock interviews.',
  },
  {
    id: 'finish',
    placement: 'center',
    eyebrow: 'You’re set',
    title: 'Go build your streak',
    body: 'Replay this tour anytime from your profile menu (Take a quick tour). Now pick a lane and ship your next solve.',
    cta: 'Start practicing',
  },
];

export const isTourDone = () => {
  try {
    return localStorage.getItem(TOUR_STORAGE_KEY) === '1';
  } catch {
    return true;
  }
};

const markTourDone = () => {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(TOUR_COMPLETE_EVENT));
};

const ProductTour = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [forced, setForced] = useState(false);

  // Home marketing tour only — /problems has its own ProblemsPageTour
  const isHome = location.pathname === '/' || location.pathname === '';
  const isImmersive = location.pathname.startsWith('/live-coding');

  const visibleSteps = useMemo(() => {
    if (isMobile) {
      return steps.filter((s) => s.id !== 'live' && s.id !== 'list');
    }
    return steps;
  }, [isMobile]);

  const step = visibleSteps[stepIndex] || visibleSteps[0];
  const progress = ((stepIndex + 1) / visibleSteps.length) * 100;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === visibleSteps.length - 1;

  const closeTour = useCallback((completed = true) => {
    setOpen(false);
    setForced(false);
    setStepIndex(0);
    setRect(null);
    if (completed) markTourDone();
  }, []);

  const measure = useCallback(() => {
    const current = visibleSteps[stepIndex];
    if (!current?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(current.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({
      behavior: 'smooth',
      block: isMobile ? 'center' : 'center',
      inline: 'nearest',
    });
    setRect(getElementTourRect(current.target));
  }, [stepIndex, visibleSteps, isMobile]);

  // Skip steps whose targets are missing (e.g. marketing sections hidden)
  useEffect(() => {
    if (!open) return undefined;
    const current = visibleSteps[stepIndex];
    if (!current?.target) return undefined;
    const timer = setTimeout(() => {
      if (!document.querySelector(current.target) && stepIndex < visibleSteps.length - 1) {
        setStepIndex((i) => i + 1);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [open, stepIndex, visibleSteps]);

  const startTour = useCallback(
    (force = false) => {
      if (isImmersive) return;
      if (!force && isTourDone()) return;

      const go = () => {
        setForced(force);
        setStepIndex(0);
        setOpen(true);
      };

      if (!isHome) {
        navigate('/');
        setTimeout(go, 500);
        return;
      }
      go();
    },
    [isHome, isImmersive, navigate],
  );

  useEffect(() => {
    if (isImmersive || !isHome || isTourDone()) return undefined;
    const timer = setTimeout(() => startTour(false), 1000);
    return () => clearTimeout(timer);
  }, [isHome, isImmersive, startTour]);

  useEffect(() => {
    const onStart = () => startTour(true);
    window.addEventListener(TOUR_START_EVENT, onStart);
    return () => window.removeEventListener(TOUR_START_EVENT, onStart);
  }, [startTour]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(measure, 80);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const id = setInterval(measure, 500);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      clearInterval(id);
    };
  }, [open, measure, stepIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTour(true);
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepIndex, visibleSteps.length]);

  const goNext = () => {
    if (isLast) {
      closeTour(true);
      navigate('/practice-track');
      return;
    }
    setStepIndex((i) => Math.min(i + 1, visibleSteps.length - 1));
  };

  const goBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  if (!open || !step) return null;

  const isCentered = !rect || step.placement === 'center';
  const cardSx = getTourCardSx({
    rect,
    placement: step.placement,
    isMobile,
    zIndex: 1402,
  });
  const innerSx = getTourCardInnerSx(isMobile, isCentered);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400 }}>
      {/* Click-catcher / dim layer */}
      <Box
        onClick={() => closeTour(true)}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: isCentered ? alpha('#020808', 0.64) : 'transparent',
        }}
      />

      {rect && !isCentered && (
        <Box
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 2.5,
            boxShadow: `0 0 0 9999px ${alpha('#020808', 0.64)}`,
            outline: `2px solid ${alpha('#7ed4d4', 0.95)}`,
            zIndex: 1401,
            pointerEvents: 'none',
            transition:
              'top 0.22s ease, left 0.22s ease, width 0.22s ease, height 0.22s ease',
          }}
        />
      )}

      <Box sx={cardSx} onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <Box
            key={step.id}
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            sx={innerSx}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${isDark ? alpha('#7ed4d4', 0.25) : alpha(ACCENT, 0.2)}`,
                bgcolor: isDark ? '#121a1c' : '#ffffff',
                boxShadow: isDark
                  ? '0 24px 64px rgba(0,0,0,0.55)'
                  : '0 24px 64px rgba(15,60,70,0.18)',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 3,
                  bgcolor: alpha(ACCENT, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: ACCENT },
                }}
              />

              <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1,
                      py: 0.4,
                      borderRadius: 99,
                      bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                      color: isDark ? '#7ed4d4' : ACCENT,
                      maxWidth: '85%',
                    }}
                  >
                    {isLast ? (
                      <RocketLaunchOutlinedIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.eyebrow}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => closeTour(true)}
                    aria-label="Skip tour"
                    sx={{ flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    letterSpacing: '-0.03em',
                    mb: 0.75,
                    lineHeight: 1.25,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    color: isDark ? '#8b949e' : '#5f6b73',
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    lineHeight: 1.55,
                    mb: 2,
                  }}
                >
                  {step.body}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? '#6e7681' : '#8a969c', fontWeight: 700 }}
                  >
                    {stepIndex + 1} / {visibleSteps.length}
                    {forced ? ' · replay' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    {!isFirst && (
                      <Button
                        size="small"
                        onClick={goBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: isDark ? '#c9d1d9' : '#334',
                        }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={goNext}
                      endIcon={!isLast ? <ArrowForwardIcon /> : undefined}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: isLast ? PINK : ACCENT,
                        px: 1.75,
                        '&:hover': { bgcolor: isLast ? '#c40048' : '#0f5c5e' },
                      }}
                    >
                      {step.cta || (isLast ? 'Finish' : 'Next')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default ProductTour;
